/**
 * The one way an error is identified.
 *
 * A fingerprint's whole job is to answer "is this the same bug as that one?", and
 * ours could not. Five near-identical hash functions (three server, one client, one
 * infrastructure) all fed the RAW message and the RAW file path into the same
 * 32-bit hash, so a fingerprint changed whenever anything incidental changed:
 *
 *   - a record id inside the message: "Indexed listing 105001427 in Elasticsearch"
 *     minted a new fingerprint per listing, 51 of them in one day for one line of code
 *   - a counter: "Iframe 1".."Iframe 5" was five bugs
 *   - an opaque partner id: one `createVendoo` refusal held 353 fingerprints over
 *     415 occurrences, because a Vendoo push id is not hex and not a number
 *   - the built asset path: `_app-ba8c05e6d474139f.js` becomes `_app-d798e5e0…js` on
 *     the next deploy, so every cluster splits again at every release
 *
 * One ad-vendor defect held 37 fingerprints and 176 occurrences on 2026-08-28 — a
 * quarter of the whole error table, invisible as a single problem on /dash/errors.
 *
 * So the identity is computed from the message and location with the incidental
 * parts folded out. Deduplication does NOT use this: see `errorDedupeKey`.
 */

/** How much of a normalized message contributes to the identity. */
export const MESSAGE_KEY_LENGTH = 200;

// Broadest patterns first, so a narrower one cannot eat half a token the broader
// one owns (a URL containing an email, a JWT that also matches the long-hex rule).
/**
 * A stack trace that arrived INSIDE the message.
 *
 * The console interceptor serialises every argument, so a library that logs an
 * error object — next-auth's `[CLIENT_FETCH_ERROR] ... {"error":{"message","stack"}}`
 * — puts the minified stack in the message text. Frame names are build output:
 * the same aborted `/api/auth/session` fetch reported `at window.fetch (` until
 * the 2026-09-02 deploy renamed the wrapper and it became `at e.fetch (`. The
 * frame sits inside the first 200 characters, so the identity rotated, the ledger
 * saw a brand-new cluster with a one-day baseline, and the next day's ordinary
 * volume read as a 3x spike and woke the triage — six fingerprints for one
 * three-week-old noise event. The stack is the occurrence, not the bug: the
 * message it heads is what identifies the failure.
 *
 * Two shapes. `JSON_STACK` is the serialised form, `"stack":"..."` — the value
 * runs to the closing quote, or to the end of a message that was clipped
 * mid-stack. `STACK_FRAMES` is a raw multi-line stack: every `at ...` frame
 * line, whether the newline is real or the two-character `\n` of a JSON string.
 */
const JSON_STACK = /"stack"\s*:\s*"(?:[^"\\]|\\.)*(?:"|$)/g;
const STACK_FRAMES = /(?:\r?\n|\\n)\s*at\s+(?:[^\r\n"\\]|\\(?!n))*/g;
const JWT = /\beyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)?/g;
const URL_LIKE = /\bhttps?:\/\/[^\s"'<>)\\]+/gi;
const EMAIL = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
/**
 * A long hex token: a content hash, a trace id, a 40-hex partner id.
 *
 * The lookahead demanding at least one `a`-`f` is the whole point. Without it the
 * rule also swallowed plain decimal ids, because every digit is a hex digit — so
 * `document_missing_exception: [104936160]` folded to `<hex>` while the same line
 * with a seven-digit id folded to `N`, and one defect became two clusters decided by
 * nothing but how many digits the id happened to have. Decimal now falls through to
 * `NUMBER`, where it belongs.
 */
const LONG_HEX = /\b(?=[0-9a-f]*[a-f])[0-9a-f]{8,}\b/gi;
/**
 * A base62 push id — Firebase's shape, which is what Vendoo mints for both listings
 * (`ef4L0mKU1Jrexf9oWxRm`) and users (`8Yv3rrp60je0FwxQI06nX6jaY6A2`). Not hex, so
 * `LONG_HEX` never reached it, and every id in a message was its own bug: over the 14
 * days to 2026-08-30, one `createVendoo` refusal held 353 fingerprints across 415
 * occurrences, and 208 distinct messages fold to a single shape under this rule.
 *
 * Deliberately narrow. A token qualifies only at 16+ characters AND with all three
 * of lower, upper and digit present, which no English word and no SCREAMING_CASE
 * constant (`FUNCTION_INVOCATION_TIMEOUT`) can satisfy. Checked against every
 * distinct non-info production message in that window: it touches the Vendoo ids
 * and nothing else that mattered.
 */
const OPAQUE_ID =
  /\b(?=[A-Za-z0-9_-]{16,}\b)(?=[A-Za-z0-9_-]*[a-z])(?=[A-Za-z0-9_-]*[A-Z])(?=[A-Za-z0-9_-]*\d)[A-Za-z0-9_-]{16,}\b/g;
/**
 * A counter, an id, an elapsed time — anything that varies between two reports of
 * one defect.
 *
 * No closing `\b`, and that is the whole rule. `\b\d+\b` requires a boundary on
 * both sides, and there is none between a digit and a letter glued to it, so any
 * number carrying a unit survived the fold: Prisma's pool-timeout message reports
 * how long it waited, and `after 10000ms` … `after 10018ms` minted a separate
 * identity per millisecond. On 2026-09-10 a 95-minute burst of MariaDB pool
 * exhaustion arrived as **36 fingerprints over 174 occurrences** — one incident,
 * fragmented past the point where its size was visible, and the triage that read
 * it spent its whole attention list on eight of the pieces re-raising as separate
 * three-occurrence spikes.
 *
 * Dropping only the trailing boundary keeps the fold exactly as narrow as it was
 * in the other direction: a digit run still has to START at a boundary, so a code
 * whose letters lead — `P2002`, `utf8`, `s3`, `EC2`, `v2` — is untouched and two
 * Prisma error codes cannot collapse into one. Checked against every distinct
 * non-info production message of the preceding 7 days: 132 message identities
 * become 97, and every one of the 35 that merge is that single pool-timeout
 * defect. Nothing else in production's vocabulary moves.
 */
const NUMBER = /\b\d+/g;

/**
 * A React production error's code IS its identity: #185 is an update loop, #418
 * a hydration text mismatch, #327 a hook-order fault. NUMBER folded them all to
 * `#n`, so dd527d9a9a5e9b4c held three different defects under one verdict --
 * and its "stale tab" note hid a live #185 loop on /sell. Rewritten to `R418`
 * first; NUMBER cannot match there because the digits follow a word character.
 */
const REACT_ERROR_CODE = /Minified React error #(\d+)/gi;

/**
 * Reduce a message to the part that identifies the bug rather than the occurrence.
 * Ids, urls, tokens and counters all vary between two reports of one defect, and
 * every one of them has been observed splitting a cluster in production.
 */
export function normalizeErrorMessage(message: string): string {
  return (message || '')
    .replace(JSON_STACK, '"stack":"<stack>"')
    .replace(STACK_FRAMES, '')
    .replace(JWT, '<jwt>')
    .replace(URL_LIKE, '<url>')
    .replace(EMAIL, '<email>')
    .replace(UUID, '<uuid>')
    // One placeholder for both, because the encoding of an id is not part of the
    // bug's identity. Vendoo mints listing ids in two shapes, and the createVendoo
    // refusal spent 2026-08-31 split six ways: five base62 ids folding to `<id>`
    // and one 40-hex id folding to `<hex>`, one defect on two shelves. `<uuid>`
    // stays distinct — nothing has been seen minting the same field both ways.
    .replace(LONG_HEX, '<id>')
    .replace(OPAQUE_ID, '<id>')
    .replace(REACT_ERROR_CODE, 'Minified React error R$1')
    .replace(NUMBER, 'N')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .slice(0, MESSAGE_KEY_LENGTH);
}

/**
 * Reduce a file path to a stable module identity.
 *
 * Client rows carry a hashed, deployment-stamped chunk URL
 * (`//sellwild.com/_next/static/chunks/pages/_app-ba8c05e6.js?dpl=dpl_xxx:180`) and
 * server rows a build-numbered Lambda chunk (`/var/task/.next/server/chunks/7646.js:19`).
 * Both change identity on every deploy while pointing at the same code.
 */
export function normalizeErrorLocation(file?: string | null): string {
  if (!file) return '';
  return String(file)
    // Trailing ":line:col" — the line moves with every rebuild.
    .replace(/:\d+(?::\d+)?$/, '')
    // Query string, which carries the Vercel deployment id.
    .replace(/\?[^\s]*$/, '')
    // Protocol-relative or absolute host prefix.
    .replace(/^(?:https?:)?\/\/[^/]+\//, '')
    // Lambda task root.
    .replace(/^\/?var\/task\//, '')
    // `_app-ba8c05e6d474139f.js` -> `_app.js`
    .replace(/-[0-9a-f]{8,}(\.[a-z]+)$/i, '$1')
    // `chunks/7646.js` -> `chunks/N.js`
    .replace(/\/\d+(\.[a-z]+)$/i, '/N$1');
}

/**
 * Reduce a URL or server pseudo-path to a route shape: no host, no query, and
 * numeric path segments (listing ids, user ids) folded to `:id`.
 */
export function normalizeErrorRoute(url?: string | null): string {
  if (!url) return '';
  return (
    String(url)
      .replace(/\?[\s\S]*$/, '')
      .replace(/^(?:https?:)?\/\/[^/]+/, '')
      .replace(/\/\d+(?=\/|$)/g, '/:id') || '/'
  );
}

/** djb2-xor, the hash all five callsites were already using, kept for continuity. */
function hash(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = (h << 5) - h + key.charCodeAt(i);
    h = h & h; // force 32-bit
  }
  return Math.abs(h).toString(36);
}

export interface FingerprintParts {
  /** `error.name`, via utils/error-type-name. */
  errorType: string;
  message: string;
  /**
   * Where the error belongs, at the granularity a fix would be made: an API route,
   * a `logServerError` source, a service name. Kept RAW — it is already stable, and
   * folding it would merge two genuinely different callsites.
   */
  scope?: string;
  file?: string | null;
  /**
   * Deliberately NOT part of the identity. A line number changes on every edit to
   * the file above it, which would retire a fingerprint on an unrelated commit and
   * make "first seen" meaningless. Accepted so callers do not have to remember.
   */
  line?: number;
}

/** The stable identity of a bug. Two occurrences of one defect share it across deploys. */
export function errorFingerprint(parts: FingerprintParts): string {
  return hash(
    [
      parts.scope || '',
      parts.errorType,
      normalizeErrorMessage(parts.message),
      normalizeErrorLocation(parts.file),
    ].join(':')
  );
}

/**
 * The key the 5-minute write-dedup window uses. Finer than the fingerprint on purpose.
 *
 * Dedup used to run on the fingerprint, which was fine only because the fingerprint
 * was accidentally fine-grained: the raw message, the raw file and the LINE all fed
 * into it, so two throws from different lines of one file were different errors.
 * Now that the fingerprint is deliberately coarse, deduping on it would collapse
 * fifty listings' worth of a real event into one row per five minutes, and silence
 * a second failure in the same file entirely.
 *
 * So the key keeps every specific the fingerprint drops: the raw message, the exact
 * page, the exact file and line. Dedup suppresses a genuine repeat and nothing else.
 *
 * That holds only while the message names the occurrence. When what varies lives in
 * `additional_context` instead, every occurrence shares one key and the window starts
 * suppressing *different* events: `server/shipping-estimate` logs one fixed sentence
 * for every seller with no shipping origin and puts the sellerId in the context, so on
 * 2026-08-30 twenty-one distinct sellers competed for a single five-minute slot and
 * whoever arrived second was silently dropped. `dedupeScope` is the opt-in for that
 * case — a callsite that knows which entity an occurrence is about says so, and the
 * window goes back to suppressing genuine repeats only.
 */
export function errorDedupeKey(parts: {
  fingerprint: string;
  message: string;
  pageUrl?: string | null;
  /** Raw file, un-normalized. */
  file?: string | null;
  /** Raw line. The fingerprint drops it on purpose; the dedupe key needs it. */
  line?: number | null;
  /**
   * What this occurrence is about, when the message cannot say: `seller:95093487`,
   * `listing:105006190`. Optional, and omitting it reproduces the previous key
   * exactly, so no existing callsite changes behaviour.
   */
  dedupeScope?: string | null;
}): string {
  const key = [
    parts.fingerprint,
    parts.message,
    parts.pageUrl || '',
    parts.file || '',
    parts.line ?? '',
  ].join('|');

  return parts.dedupeScope ? `${key}|${parts.dedupeScope}` : key;
}
